<template>
  <SplitViewTemplate
    :screenConfig="data"
    :backButtonRoute="'/settings/intake'"
    :dataModel="formModel"
    :formConfig="setFormConfig"
    :setLoading="setLoading"
    :validationSummary="validationSummary"
    :ref="'moisture-threshold'"
    :defaultUserSettings="false" />
</template>
<script>
import { FeatureStorage } from "@/_services/storage";
import SplitViewTemplate from "../../../templates/split-view";
import RouteMixin from "@/_mixins/route.mixin";
export default {
  mixins: [RouteMixin],
  components: {
    SplitViewTemplate,
  },
  data() {
    return {
      setLoading: false,
      validationSummary: [],
      seasonMatched: false,
      lastCheckedSeason: undefined,
      formModel: {
        Season: null,
        MoistureThresholdPcnt: null,
        ChangedBy: "",
        CreatedBy: "",
        DateChanged: "",
        DateCreated: "",
      },
      data: {
        title: "Moisture Threshold",
        pageRoute: "/moisture-threshold",
        saveEndPoint: "Save_AlmondcoMoistureThreshold",
        recordEndPoint: "AlmondcoMoistureThreshold",
        exportFileName: "Moisture Threshold List",
        recordPostProcess: this.recordPostProcess,
        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "AlmondcoMoistureThreshold",
              filter: "",
              serverPaging: true,
              allowGrouping: false,
              sort: [{ selector: "Season", desc: false }],
            },
            rowSelect: {
              view: this,
              page: "moisture-threshold",
              mode: "review",
              idProperty: "Season",
              idMode: "Number",
              rowSelectCallBack: () => {
                this.beforeInit();
              },
            },
          },

          toolbar: {
            search: true,
            columnChooser: true,
            export: false,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                route: "/moisture-threshold/create",
                clickAction: () => {
                  this.goToCreateMoistureThreshold();
                },
                buttonLocation: "left",
                visible: FeatureStorage.checkPermission(
                  "AlmondcoMoistureThreshold",
                  "create"
                ),
              },
            ],
          },
          columns: [
            {
              screenField: "MoistureThresholdSeason",
              dataField: "Season",
              label: "Season",
              allowHiding: false,
              sortIndex: 0,
              sortOrder: "asc",
              filterIndex: 0,
              alignment: "left",
              dataType: "number",
            },
            {
              screenField: "MoistureThresholdPcnt",
              dataField: "MoistureThresholdPcnt",
              label: "Moisture Threshold %",
              alignment: "left",
              format: "##0.00 '%'",
              cellTemplate: "Decimal",
            },
          ],
          valueChangedEventListners: [],
        },
      },
    };
  },
  computed: {
    setFormConfig() {
      const form = {
        headerToolbar: {
          closeSplit: true,
          save: this.setSaveOption,
          audit: true,
          auditItems: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  dataField: "FormattedDateCreated",
                  label: { text: "Created By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    disabled: true,
                  },
                },
                {
                  dataField: "FormattedDateChanged",
                  label: { text: "Changed By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    disabled: true,
                  },
                },
              ],
            },
          ],
          editCallBack: () => {
            this.beforeInit();
          },
        },
        formItems: [
          {
            itemType: "group",
            caption: "",
            colCount: 1,
            items: [
              {
                dataField: "Season",
                editorType: "dxNumberBox",
                editorOptions: {
                  tabIndex: 1,
                  format: "#",
                  min: 1,
                  max: 9999,
                  inputAttr: { style: "text-align: left" },
                  onFocusOut: (e) => {
                    this.onSeasonBlur(e);
                  },
                },
                required: true,
                isPrimaryKeyField: true,
                label: { text: "Season" },
                visible: true,
                allowUpdate:
                  this.baseView.mode == "create" && !this.seasonMatched,
              },
              {
                dataField: "MoistureThresholdPcnt",
                editorType: "dxNumberBox",
                editorOptions: {
                  tabIndex: 2,
                  format: "#0.00",
                  min: 0,
                  max: 99.99,
                },
                label: { text: "Moisture Threshold %" },
                visible: true,
              },
            ],
          },
        ],
        overWritePayload: this.overWritePayload,
      };

      return form;
    },
    setSaveOption() {
      // NOTE: technical-design.md section 6 literally says 'update' here, but
      // FeatureStorage.checkPermission only recognises 'create'/'edit'/'review' -
      // 'update' matches neither branch and would always return false, hiding
      // Save in edit mode. Using 'edit', matching every sibling Intake Settings
      // screen and the real checkPermission implementation. Flag the doc for
      // correction rather than porting its typo into working code.
      return FeatureStorage.checkPermission(
        this.$route.meta.applicationFeatureCode,
        this.baseView.mode == "create" ? "create" : "edit"
      );
    },
  },
  methods: {
    goToCreateMoistureThreshold() {
      this.$router
        .push({
          path: `/moisture-threshold/create`,
        })
        .then(() => {
          this.beforeInit();
        });
    },
    beforeInit() {
      this.seasonMatched = false;
      this.lastCheckedSeason = undefined;
    },
    /**
     * Returns the grid's currently-loaded rows (client-side only - remote
     * paging may not hold every Season, an accepted, non-blocking staleness
     * risk per technical-design.md section 5/section 11 Q-001). The get-or-create
     * match deliberately reads this instead of an extra server round-trip.
     */
    getGridItems() {
      const splitView = this.$refs["moisture-threshold"];
      const grid =
        splitView && splitView.$refs && splitView.$refs["split-view-grid"];
      return grid && grid.getDataSource ? grid.getDataSource() : [];
    },
    /**
     * BR-01/BR-02: on blur during Add New, an existing Season loads its
     * Moisture Threshold % and locks the field; an unmatched Season is left
     * ready to create. BR-03: does not re-check an unchanged Season.
     */
    onSeasonBlur() {
      if (this.baseView.mode !== "create") return;

      const season = this.formModel.Season;
      if (season === null || season === undefined || season === "") return;
      if (this.lastCheckedSeason === season) return;
      this.lastCheckedSeason = season;

      const match = this.getGridItems().find((item) => item.Season === season);
      if (match) {
        this.formModel.MoistureThresholdPcnt = match.MoistureThresholdPcnt;
        this.seasonMatched = true;
        this.$notify(
          `Season ${season} already has a Moisture Threshold record - loaded its existing value.`,
          "info"
        );
      } else {
        this.seasonMatched = false;
      }
    },
    /**
     * Save-time re-check (technical-design.md section 6), regardless of the blur
     * outcome - covers Enter-key submission or any path that skips blur. The
     * data outcome is correct either way (the server upserts on Season - BR-07);
     * this only keeps the "creating" UX honest when a match exists.
     */
    overWritePayload(payload) {
      if (this.baseView.mode === "create" && !this.seasonMatched) {
        const match = this.getGridItems().find(
          (item) => item.Season === payload.Season
        );
        if (match) {
          this.$notify(
            `Season ${payload.Season} already has a Moisture Threshold record - it will be updated instead of created.`,
            "info"
          );
        }
      }
      return payload;
    },
    /**
     * BR-04: Moisture Threshold % is optional and defaults to 0.00. The
     * split-view template resets every formModel field to undefined on Add
     * New before this hook runs, so the default is applied here rather than
     * relying on the initial data() value.
     */
    recordPostProcess(data) {
      if (data.MoistureThresholdPcnt === undefined || data.MoistureThresholdPcnt === null) {
        data.MoistureThresholdPcnt = 0.0;
      }
      return data;
    },
  },
  created() {
    this.beforeInit();
  },
};
</script>
